"use client";

import {
  buildConsentReceipt,
  type ConsentCategories,
  type ConsentDecision,
  type ConsentReceipt,
  clearConsent,
  clearReceipt,
  defaultCategories,
  fullCategories,
  isGpcEnabled,
  persistReceipt,
  readConsent,
  readReceipt,
  writeConsent,
} from "@guru/shared/consent";
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

export interface ConsentContextValue {
  categories: ConsentCategories;
  hasDecided: boolean;
  gpc: boolean;
  acceptAll: () => void;
  rejectAll: () => void;
  save: (next: ConsentCategories) => void;
  openPreferences: () => void;
  preferencesOpen: boolean;
  setPreferencesOpen: (open: boolean) => void;
}

const ConsentContext = createContext<ConsentContextValue | null>(null);

export function useConsent(): ConsentContextValue {
  const ctx = useContext(ConsentContext);
  if (!ctx) throw new Error("useConsent must be used within a ConsentProvider");
  return ctx;
}

export interface ConsentProviderProps {
  policyVersion: string;
  /** Path to the preference centre, e.g. "/cookies". Used as the withdrawal link. */
  withdrawalLink: string;
  children: React.ReactNode;
  /** Fired whenever effective categories change so apps can opt PostHog in/out. */
  onChange?: (categories: ConsentCategories) => void;
  /** Receives every receipt for server persistence (POST /api/public/consent). */
  onPersistReceipt?: (receipt: ConsentReceipt) => void;
}

export function ConsentProvider({
  policyVersion,
  withdrawalLink,
  children,
  onChange,
  onPersistReceipt,
}: ConsentProviderProps) {
  const [categories, setCategories] = useState<ConsentCategories>(defaultCategories);
  const [hasDecided, setHasDecided] = useState(false);
  const [gpc, setGpc] = useState(false);
  const [preferencesOpen, setPreferencesOpen] = useState(false);

  const buildAndPersist = useCallback(
    (cats: ConsentCategories, decision: ConsentDecision) => {
      const receipt = buildConsentReceipt({
        categories: cats,
        decision,
        policyVersion,
        withdrawalLink,
        userAgent: typeof navigator !== "undefined" ? navigator.userAgent : "unknown",
      });
      persistReceipt(receipt);
      onPersistReceipt?.(receipt);
    },
    [policyVersion, withdrawalLink, onPersistReceipt]
  );

  useEffect(() => {
    const stored = readConsent();
    const gpcOn = isGpcEnabled();
    setGpc(gpcOn);
    // GPC always wins: deny everything regardless of any stored decision, and
    // suppress the banner so we never auto-track a privacy-signalling visitor.
    if (gpcOn) {
      const denied = defaultCategories();
      setCategories(denied);
      onChange?.(denied);
      // Record a GPC-driven denial receipt once for the audit trail.
      if (!readReceipt()) buildAndPersist(denied, "gpc");
      return;
    }
    if (stored) {
      setCategories(stored.categories);
      setHasDecided(true);
      onChange?.(stored.categories);
    }
  }, [onChange, buildAndPersist]);

  const commit = useCallback(
    (next: ConsentCategories, decision: ConsentDecision) => {
      // GPC overrides any choice — saving from the preference centre cannot
      // enable tracking while the browser signals Global Privacy Control.
      const gpcOn = isGpcEnabled();
      const effective = gpcOn ? defaultCategories() : next;
      const effectiveDecision: ConsentDecision = gpcOn ? "gpc" : decision;
      writeConsent(effective, effectiveDecision, policyVersion);
      buildAndPersist(effective, effectiveDecision);
      setCategories(effective);
      setHasDecided(!gpcOn);
      onChange?.(effective);
    },
    [policyVersion, onChange, buildAndPersist]
  );

  const acceptAll = useCallback(() => commit(fullCategories(), "accepted"), [commit]);
  const rejectAll = useCallback(() => commit(defaultCategories(), "rejected"), [commit]);
  const save = useCallback(
    (next: ConsentCategories) => {
      commit(next, "customised");
      setPreferencesOpen(false);
    },
    [commit]
  );
  const openPreferences = useCallback(() => setPreferencesOpen(true), []);

  const value = useMemo<ConsentContextValue>(
    () => ({
      categories,
      hasDecided,
      gpc,
      acceptAll,
      rejectAll,
      save,
      openPreferences,
      preferencesOpen,
      setPreferencesOpen,
    }),
    [categories, hasDecided, gpc, acceptAll, rejectAll, save, openPreferences, preferencesOpen]
  );

  return <ConsentContext.Provider value={value}>{children}</ConsentContext.Provider>;
}

export function resetConsent(): void {
  clearConsent();
  clearReceipt();
}
